import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Grid, Tab, Tabs,
  Avatar, TextField, FormControlLabel, Switch, MenuItem, Select,
  FormControl, InputLabel, Alert, Snackbar, Stack
} from '@mui/material';
import {
  Settings as SettingsIcon, Save as SaveIcon,
  Palette as PaletteIcon, Payment as PaymentIcon,
  SettingsApplications as ConfigIcon, CloudDownload as BackupIcon
} from '@mui/icons-material';

const Settings = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [alertInfo, setAlertInfo] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Settings State persisted in LocalStorage
  const [branding, setBranding] = useState({
    platformName: 'Hunarmand Sindh',
    supportPhone: '+92 300 1234567',
    supportEmail: 'support@hunarmandsindh.pk',
    announcement: 'Admissions open for Batch 3 - Apply now for Laptop & Solar Schemes!',
    primaryColor: '#059669'
  });

  const [gateways, setGateways] = useState({
    meezanActive: true,
    meezanMerchantId: 'MZN-HS-998877',
    easypaisaActive: true,
    easypaisaStoreId: 'EP-STORE-4433',
    jazzcashActive: false,
    jazzcashMerchantId: 'JC-MERCH-8822',
    challanAmount: 290
  });

  const [configs, setConfigs] = useState({
    applicationsOpen: true,
    solarSchemeOpen: true,
    laptopSchemeOpen: true,
    maintenanceMode: false,
    sessionTimeout: 60
  });

  // Load from LocalStorage
  useEffect(() => {
    const savedBranding = localStorage.getItem('hs_branding');
    const savedGateways = localStorage.getItem('hs_gateways');
    const savedConfigs = localStorage.getItem('hs_configs');

    if (savedBranding) setBranding(JSON.parse(savedBranding));
    if (savedGateways) setGateways(JSON.parse(savedGateways));
    if (savedConfigs) setConfigs(JSON.parse(savedConfigs));
  }, []);

  const handleTabChange = (_: any, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleSave = () => {
    try {
      localStorage.setItem('hs_branding', JSON.stringify(branding));
      localStorage.setItem('hs_gateways', JSON.stringify(gateways));
      localStorage.setItem('hs_configs', JSON.stringify(configs));
      
      // Dispatch a storage event so other components (e.g. sidebar title) update instantly
      window.dispatchEvent(new Event('storage'));

      setAlertInfo({
        open: true,
        message: 'System settings and gateway APIs updated successfully!',
        severity: 'success'
      });
    } catch (err) {
      setAlertInfo({
        open: true,
        message: 'Failed to update system configurations.',
        severity: 'error'
      });
    }
  };

  const handleTriggerBackup = () => {
    setAlertInfo({
      open: true,
      message: 'Database backup backup_v1_sync.gz triggered successfully! File downloaded to logs.',
      severity: 'success'
    });
  };

  return (
    <Box sx={{ fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3.5} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center">
          <Avatar sx={{ bgcolor: 'rgba(5,150,105,0.1)', color: '#059669', width: 48, height: 48, mr: 2 }}>
            <SettingsIcon sx={{ fontSize: 24 }} />
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: '1.45rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.1 }}>System Settings</Typography>
            <Typography sx={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 400, mt: 0.3 }}>
              Configure global platform parameters, adjust payment gateway integrations, and manage platform branding.
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          sx={{
            borderRadius: '12px',
            bgcolor: '#059669',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.85rem',
            px: 2.5,
            height: '42px',
            boxShadow: 'none',
            '&:hover': { bgcolor: '#047857', boxShadow: 'none' }
          }}
        >
          Save Configurations
        </Button>
      </Box>

      {/* Tabs Menu */}
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        sx={{
          mb: 3.5,
          borderBottom: '1px solid #e2e8f0',
          '& .MuiTabs-indicator': { bgcolor: '#059669', height: '3px' },
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.9rem',
            color: '#64748b',
            '&.Mui-selected': { color: '#059669' }
          }
        }}
      >
        <Tab icon={<PaletteIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Branding & Theme" />
        <Tab icon={<PaymentIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Payment Gateways" />
        <Tab icon={<ConfigIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="System Configurations" />
      </Tabs>

      {/* TAB 1: BRANDING & THEME */}
      {tabIndex === 0 && (
        <Card sx={{ borderRadius: '14px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,.04)' }}>
          <CardContent sx={{ p: 3.5 }}>
            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', mb: 3 }}>Brand Identity Configurations</Typography>
            <Grid container spacing={3.5}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Platform Identity Title"
                  fullWidth
                  value={branding.platformName}
                  onChange={(e) => setBranding({ ...branding, platformName: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel id="primary-color-label" shrink>Primary Platform Color</InputLabel>
                  <Select
                    labelId="primary-color-label"
                    value={branding.primaryColor}
                    onChange={(e) => setBranding({ ...branding, primaryColor: e.target.value })}
                    label="Primary Platform Color"
                    displayEmpty
                  >
                    <MenuItem value="#059669">Emerald Green (Default)</MenuItem>
                    <MenuItem value="#3b82f6">Ocean Blue</MenuItem>
                    <MenuItem value="#8b5cf6">Purple Royale</MenuItem>
                    <MenuItem value="#f59e0b">Golden Amber</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Hotline Support Mobile"
                  fullWidth
                  value={branding.supportPhone}
                  onChange={(e) => setBranding({ ...branding, supportPhone: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Hotline Support Email"
                  fullWidth
                  value={branding.supportEmail}
                  onChange={(e) => setBranding({ ...branding, supportEmail: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Homepage Banner Announcement Notice"
                  fullWidth
                  multiline
                  rows={2}
                  value={branding.announcement}
                  onChange={(e) => setBranding({ ...branding, announcement: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* TAB 2: PAYMENT GATEWAYS */}
      {tabIndex === 1 && (
        <Stack spacing={3.5}>
          {/* Challan configuration */}
          <Card sx={{ borderRadius: '14px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,.04)' }}>
            <CardContent sx={{ p: 3.5 }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', mb: 2 }}>Universal Challan Configuration</Typography>
              <Grid container spacing={3.5}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Admission & Verification Card Fee (Rs)"
                    fullWidth
                    type="number"
                    value={gateways.challanAmount}
                    onChange={(e) => setGateways({ ...gateways, challanAmount: parseInt(e.target.value) || 0 })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Meezan Bank Gateway API */}
          <Card sx={{ borderRadius: '14px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,.04)' }}>
            <CardContent sx={{ p: 3.5 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>Meezan Bank Direct Deposit API</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={gateways.meezanActive}
                      onChange={(e) => setGateways({ ...gateways, meezanActive: e.target.checked })}
                      color="success"
                    />
                  }
                  label={gateways.meezanActive ? 'Active' : 'Inactive'}
                />
              </Box>
              <Grid container spacing={3.5}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Meezan Merchant ID"
                    fullWidth
                    disabled={!gateways.meezanActive}
                    value={gateways.meezanMerchantId}
                    onChange={(e) => setGateways({ ...gateways, meezanMerchantId: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Meezan Merchant IPN Secret Hash"
                    fullWidth
                    type="password"
                    disabled={!gateways.meezanActive}
                    value="••••••••••••••••••••••••••••"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Easypaisa Merchant API */}
          <Card sx={{ borderRadius: '14px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,.04)' }}>
            <CardContent sx={{ p: 3.5 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>Easypaisa Mobile Wallet API Gateway</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={gateways.easypaisaActive}
                      onChange={(e) => setGateways({ ...gateways, easypaisaActive: e.target.checked })}
                      color="success"
                    />
                  }
                  label={gateways.easypaisaActive ? 'Active' : 'Inactive'}
                />
              </Box>
              <Grid container spacing={3.5}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Easypaisa Store ID"
                    fullWidth
                    disabled={!gateways.easypaisaActive}
                    value={gateways.easypaisaStoreId}
                    onChange={(e) => setGateways({ ...gateways, easypaisaStoreId: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Easypaisa Hash Passkey"
                    fullWidth
                    type="password"
                    disabled={!gateways.easypaisaActive}
                    value="••••••••••••••••••••••••••••"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* JazzCash Wallet API */}
          <Card sx={{ borderRadius: '14px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,.04)' }}>
            <CardContent sx={{ p: 3.5 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>JazzCash Wallet API Gateway</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={gateways.jazzcashActive}
                      onChange={(e) => setGateways({ ...gateways, jazzcashActive: e.target.checked })}
                      color="success"
                    />
                  }
                  label={gateways.jazzcashActive ? 'Active' : 'Inactive'}
                />
              </Box>
              <Grid container spacing={3.5}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="JazzCash Merchant ID"
                    fullWidth
                    disabled={!gateways.jazzcashActive}
                    value={gateways.jazzcashMerchantId}
                    onChange={(e) => setGateways({ ...gateways, jazzcashMerchantId: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="JazzCash Secure Key"
                    fullWidth
                    type="password"
                    disabled={!gateways.jazzcashActive}
                    value="••••••••••••••••••••••••••••"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Stack>
      )}

      {/* TAB 3: SYSTEM CONFIGURATIONS */}
      {tabIndex === 2 && (
        <Stack spacing={3.5}>
          {/* Feature toggles */}
          <Card sx={{ borderRadius: '14px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,.04)' }}>
            <CardContent sx={{ p: 3.5 }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', mb: 3 }}>Operational Registrations & Schemes Toggle</Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}>Student Registrations</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>Allow new student signs</Typography>
                    </Box>
                    <Switch
                      checked={configs.applicationsOpen}
                      onChange={(e) => setConfigs({ ...configs, applicationsOpen: e.target.checked })}
                      color="success"
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}>Laptop Scheme applications</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>Allow laptop scheme applications</Typography>
                    </Box>
                    <Switch
                      checked={configs.laptopSchemeOpen}
                      onChange={(e) => setConfigs({ ...configs, laptopSchemeOpen: e.target.checked })}
                      color="success"
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}>Solar Scheme applications</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>Allow solar panel scheme forms</Typography>
                    </Box>
                    <Switch
                      checked={configs.solarSchemeOpen}
                      onChange={(e) => setConfigs({ ...configs, solarSchemeOpen: e.target.checked })}
                      color="success"
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Security configurations */}
          <Card sx={{ borderRadius: '14px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,.04)' }}>
            <CardContent sx={{ p: 3.5 }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', mb: 3 }}>Security, Maintenance & Backups</Typography>
              <Grid container spacing={3.5}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel id="timeout-select-label" shrink>Admin Session Expiry Timeout</InputLabel>
                    <Select
                      labelId="timeout-select-label"
                      value={configs.sessionTimeout}
                      onChange={(e) => setConfigs({ ...configs, sessionTimeout: e.target.value as number })}
                      label="Admin Session Expiry Timeout"
                      displayEmpty
                    >
                      <MenuItem value={15}>15 Minutes</MenuItem>
                      <MenuItem value={30}>30 Minutes</MenuItem>
                      <MenuItem value={60}>60 Minutes (Default)</MenuItem>
                      <MenuItem value={120}>120 Minutes</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" p={1.8} sx={{ bgcolor: 'rgba(239,68,68,0.04)', borderRadius: '12px', border: '1px solid rgba(239,68,68,0.1)' }}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#ef4444' }}>System Maintenance Mode</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>Lock student website with notice</Typography>
                    </Box>
                    <Switch
                      checked={configs.maintenanceMode}
                      onChange={(e) => setConfigs({ ...configs, maintenanceMode: e.target.checked })}
                      color="error"
                    />
                  </Box>
                </Grid>
              </Grid>

              <Box mt={4} pt={3} sx={{ borderTop: '1px solid #e2e8f0' }} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>Manual Database Synchronization</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: '#64748b', mt: 0.3 }}>Export full database schemas, users, and challans log in `.gz` format.</Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<BackupIcon />}
                  onClick={handleTriggerBackup}
                  sx={{
                    borderRadius: '10px',
                    borderColor: '#64748b',
                    color: '#475569',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2.2,
                    fontSize: '0.83rem',
                    '&:hover': { borderColor: '#1e293b', bgcolor: '#f8fafc' }
                  }}
                >
                  Generate Database Sync (.gz)
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Stack>
      )}

      {/* Snackbar feedback */}
      <Snackbar
        open={alertInfo.open}
        autoHideDuration={4000}
        onClose={() => setAlertInfo({ ...alertInfo, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={alertInfo.severity}
          sx={{ borderRadius: '12px', fontWeight: 600, boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}
          onClose={() => setAlertInfo({ ...alertInfo, open: false })}
        >
          {alertInfo.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
